      
import 'flatpickr/dist/flatpickr.min.css'
import MonthSelectPlugin from 'flatpickr/dist/plugins/monthSelect'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Calendar, X } from 'react-feather'
import Flatpickr from 'react-flatpickr'
import InputMask from 'react-input-mask'
import { Button, InputGroup } from 'reactstrap'
import './datepicker.css'

// e.g. "Okt 2026", matching the Indonesian month grid
const monthLabel = (date) =>
  date.toLocaleDateString('id-ID', { month: 'short', year: 'numeric' })

/**
 * Helper: Format date to DD/MM/YYYY string
 */
const formatDateToDMY = (date) => {
  if (!date || isNaN(date.getTime())) return null
  const d = String(date.getDate()).padStart(2, '0')
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const y = date.getFullYear()
  return `${d}/${m}/${y}`
}

/**
 * Helper: Parse DD/MM/YYYY string to Date
 */
const parseDMYtoDate = (dateStr) => {
  if (!dateStr || dateStr.length !== 10) return null
  const [d, m, y] = dateStr.split('/').map(Number)
  if (isNaN(d) || isNaN(m) || isNaN(y)) return null
  const date = new Date(y, m - 1, d)
  if (isNaN(date.getTime())) return null
  return date
}

/**
 * Helper: Check if a date string is complete (no placeholder chars)
 */
const isDateComplete = (dateStr) => {
  return dateStr && /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)
}

/**
 * Helper: Extract two dates from masked input "DD/MM/YYYY - DD/MM/YYYY"
 */
const extractDatesFromMasked = (maskedInput) => {
  const parts = maskedInput.split(' - ')
  if (parts.length !== 2) return { startStr: null, endStr: null }
  return {
    startStr: parts[0],
    endStr: parts[1],
  }
}

/**
 * Helper: Format date to DD-MM-YYYY string
 */
const formatDateToDDMMYYYY = (date) => {
  if (!date || isNaN(date.getTime())) return null
  const d = String(date.getDate()).padStart(2, '0')
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const y = date.getFullYear()
  return `${d}-${m}-${y}`
}

/**
 * Helper: Format date to YYYY-MM-DD string (ISO date only, no time)
 */
const formatDateToYYYYMMDD = (date) => {
  if (!date || isNaN(date.getTime())) return null
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Helper: Format date to ISO string (includes time: YYYY-MM-DDTHH:mm:ss.sssZ)
 */
const formatDateToISO = (date) => {
  if (!date || isNaN(date.getTime())) return null
  return date.toISOString()
}

/**
 * Helper: Set date to start of day (00:00:00)
 */
const setStartOfDay = (date) => {
  if (!date || isNaN(date.getTime())) return null
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

/**
 * Helper: Set date to end of day (23:59:59)
 */
const setEndOfDay = (date) => {
  if (!date || isNaN(date.getTime())) return null
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

/**
 * Helper: Format date with specified format
 * @param {Date} date - Date object
 * @param {string} format - 'dd-mm-yyyy' | 'yyyy-mm-dd' | 'iso'
 * @returns {string} Formatted date string or null
 */
const formatDateWithFormat = (date, format = 'yyyy-mm-dd') => {
  if (!date || isNaN(date.getTime())) return null

  switch (format.toLowerCase()) {
    case 'dd-mm-yyyy':
      return formatDateToDDMMYYYY(date)
    case 'yyyy-mm-dd':
      return formatDateToYYYYMMDD(date)
    case 'iso':
      return formatDateToISO(date)
    default:
      return formatDateToYYYYMMDD(date) // default to YYYY-MM-DD
  }
}

/**
 * PMB Date Range Picker Component - Range Selection with masked input + calendar
 * @param {array} value - [startDate, endDate] array in 'YYYY-MM-DD' format
 * @param {function} onChange - Callback when both dates are valid, returns [formatted start, formatted end]
 * @param {string} label - Input label
 * @param {boolean} disabled - Disable input
 * @param {string} format - Output format: 'dd-mm-yyyy' | 'yyyy-mm-dd' | 'iso' (default: 'yyyy-mm-dd')
 */
/**
 * PMB Date Range Picker Component
 * @param {Array} value - Array of date strings in 'YYYY-MM-DD' format [startDate, endDate]
 * @param {function} onChange - Callback when dates are selected, returns formatted date array or null
 * @param {function} onClear - Callback when clear button is clicked. If not provided, just clears input
 * @param {string} label - Input label
 * @param {boolean} disabled - Disable input
 * @param {string} format - Output format: 'dd-mm-yyyy' | 'yyyy-mm-dd' | 'iso' (default: 'yyyy-mm-dd')
 * @param {string} clearMode - 'empty' (clear completely) | 'callback' (call onClear for custom handling). Default: 'empty'
 */
export const PMBDateRangePicker = ({
  value,
  onChange,
  onClear,
  label = 'Tanggal',
  disabled = false,
  format = 'yyyy-mm-dd',
  clearMode = 'empty',
  testId = 'pmb-date-range',
  ...props
}) => {
  const [dates, setDates] = useState([])
  const [inputValue, setInputValue] = useState('')
  const flatpickrRef = useRef(null)
  const inputRef = useRef(null)

  // Initialize from external value prop
  useEffect(() => {
    if (Array.isArray(value) && value[0] && value[1]) {
      const start = new Date(value[0])
      const end = new Date(value[1])
      setDates([start, end])
      const startDMY = formatDateToDMY(start)
      const endDMY = formatDateToDMY(end)
      if (startDMY && endDMY) {
        setInputValue(`${startDMY} - ${endDMY}`)
      }
    } else if (
      !value ||
      (Array.isArray(value) &&
        (value.length === 0 || (!value[0] && !value[1])))
    ) {
      setDates([])
      setInputValue('')
    }
  }, [value])

  /**
   * Called when flatpickr calendar selection changes
   */
  const handleCalendarChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      let [start, end] = selectedDates

      // Always apply time adjustments
      start = setStartOfDay(start)
      end = setEndOfDay(end)

      setDates([start, end])

      // Update input with formatted dates (always show DD/MM/YYYY for input)
      const startDMY = formatDateToDMY(start)
      const endDMY = formatDateToDMY(end)
      const newInput = `${startDMY} - ${endDMY}`
      setInputValue(newInput)

      // Call onChange with requested format
      const startFormatted = formatDateWithFormat(start, format)
      const endFormatted = formatDateWithFormat(end, format)
      if (onChange) {
        onChange([startFormatted, endFormatted])
      }
    }
  }

  /**
   * Called when masked input value changes
   */
  const handleInputChange = (e) => {
    const masked = e.target.value
    setInputValue(masked)

    // Extract the two date parts
    const { startStr, endStr } = extractDatesFromMasked(masked)

    // Only call onChange if BOTH dates are complete
    if (isDateComplete(startStr) && isDateComplete(endStr)) {
      let start = parseDMYtoDate(startStr)
      let end = parseDMYtoDate(endStr)

      if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
        // Always apply time adjustments
        start = setStartOfDay(start)
        end = setEndOfDay(end)

        setDates([start, end])

        // Update flatpickr silently
        if (flatpickrRef.current?.flatpickr) {
          flatpickrRef.current.flatpickr.setDate([start, end], false)
        }

        // Call onChange with requested format
        const startFormatted = formatDateWithFormat(start, format)
        const endFormatted = formatDateWithFormat(end, format)
        if (onChange) {
          onChange([startFormatted, endFormatted])
        }
      }
    }
    // If incomplete, do NOT call onChange
  }

  /**
   * Called when input is focused - open flatpickr
   */
  const handleInputFocus = () => {
    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.open()
    }
  }

  /**
   * Open calendar picker
   */
  const openCalendar = () => {
    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.open()
    }
  }

  /**
   * Clear dates
   * clearMode 'empty': Clear input and dates completely
   * clearMode 'callback': Just call onClear callback - parent component handles reset logic
   */
  const handleClear = () => {
    if (clearMode === 'callback' && onClear) {
      // Only call callback, don't clear the input
      onClear()
    } else {
      // Default behavior: clear input and dates
      setInputValue('')
      setDates([])
      if (onChange) {
        onChange(null)
      }
      // Also call onClear if provided
      if (onClear) {
        onClear()
      }
    }
  }

  return (
    <div className="pmb-date-range-picker" data-testid={testId} {...props}>
      {label && (
        <label className="d-block mb-2" data-testid={`${testId}-label`}>
          {label}
        </label>
      )}

      <InputGroup>
        <InputMask
          ref={inputRef}
          mask="99/99/9999 - 99/99/9999"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder="__/__/____ - __/__/____"
          disabled={disabled}
          maskChar={null}
          className="form-control"
          data-testid={`${testId}-input`}
        />
        {inputValue !== '' && (
          <Button
            color="secondary"
            outline
            onClick={handleClear}
            disabled={disabled}
            type="button"
            className="pmb-clear-btn"
            data-testid={`${testId}-clear`}
          >
            <X size={18} />
          </Button>
        )}
        <Button
          color="secondary"
          outline
          onClick={openCalendar}
          disabled={disabled}
          type="button"
          className="pmb-calendar-btn"
          data-testid={`${testId}-calendar`}
        >
          <Calendar size={18} />
        </Button>
      </InputGroup>

      <div style={{ position: 'relative', visibility: 'hidden', height: 0 }}>
        <Flatpickr
          ref={flatpickrRef}
          value={dates}
          onChange={handleCalendarChange}
          options={{
            mode: 'range',
            dateFormat: 'd/m/Y',
            monthSelectorType: 'static',
            appendTo: document.body,
            position: 'below',
          }}
          disabled={disabled}
        />
      </div>
    </div>
  )
}

/**
 * PMB Single Date Picker Component
 * @param {string} value - ISO date string in 'YYYY-MM-DD' format
 * @param {function} onChange - Callback when date is valid, returns formatted date string
 * @param {string} label - Input label
 * @param {boolean} disabled - Disable input
 * @param {string} format - Output format: 'dd-mm-yyyy' | 'yyyy-mm-dd' | 'iso' (default: 'yyyy-mm-dd')
 */
export const PMBDatePicker = ({
  value,
  onChange,
  label = 'Pilih Tanggal',
  disabled = false,
  format = 'yyyy-mm-dd',
  testId = 'pmb-date',
  ...props
}) => {
  const [selectedDate, setSelectedDate] = useState([])
  const [inputValue, setInputValue] = useState('')
  const flatpickrRef = useRef(null)
  const inputRef = useRef(null)

  // Initialize from external value prop
  useEffect(() => {
    if (value) {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        setSelectedDate([date])
        const dateDMY = formatDateToDMY(date)
        if (dateDMY) {
          setInputValue(dateDMY)
        }
      }
    } else {
      setSelectedDate([])
      setInputValue('')
    }
  }, [value])

  /**
   * Called when flatpickr calendar selection changes
   */
  const handleCalendarChange = (selectedDates) => {
    if (selectedDates.length > 0) {
      let date = selectedDates[0]

      // Always apply time adjustment
      date = setStartOfDay(date)

      setSelectedDate([date])

      // Update input with formatted date (always show DD/MM/YYYY for input)
      const dateDMY = formatDateToDMY(date)
      if (dateDMY) {
        setInputValue(dateDMY)
      }

      // Call onChange with requested format
      const dateFormatted = formatDateWithFormat(date, format)
      if (onChange) {
        onChange(dateFormatted)
      }
    }
  }

  /**
   * Called when masked input value changes
   */
  const handleInputChange = (e) => {
    const masked = e.target.value
    setInputValue(masked)

    // Only call onChange if date is complete
    if (isDateComplete(masked)) {
      let date = parseDMYtoDate(masked)

      if (date && !isNaN(date.getTime())) {
        // Always apply time adjustment
        date = setStartOfDay(date)

        setSelectedDate([date])

        // Update flatpickr silently
        if (flatpickrRef.current?.flatpickr) {
          flatpickrRef.current.flatpickr.setDate(date, false)
        }

        // Call onChange with requested format
        const dateFormatted = formatDateWithFormat(date, format)
        if (onChange) {
          onChange(dateFormatted)
        }
      }
    }
    // If incomplete, do NOT call onChange
  }

  /**
   * Called when input is focused - open flatpickr
   */
  const handleInputFocus = () => {
    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.open()
    }
  }

  /**
   * Open calendar picker
   */
  const openCalendar = () => {
    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.open()
    }
  }

  /**
   * Clear date
   */
  const handleClear = () => {
    setInputValue('')
    setSelectedDate([])
    if (onChange) {
      onChange(null)
    }
  }

  return (
    <div className="pmb-date-picker" data-testid={testId} {...props}>
      {label && (
        <label className="d-block mb-2 fw-semibold" data-testid={`${testId}-label`}>
          {label}
        </label>
      )}

      <InputGroup>
        <InputMask
          ref={inputRef}
          mask="99/99/9999"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder="__/__/____"
          disabled={disabled}
          maskChar={null}
          className="form-control"
          data-testid={`${testId}-input`}
        />
        {inputValue !== '' && (
          <Button
            color="secondary"
            outline
            onClick={handleClear}
            disabled={disabled}
            type="button"
            className="pmb-clear-btn"
            data-testid={`${testId}-clear`}
          >
            <X size={18} />
          </Button>
        )}
        <Button
          color="secondary"
          outline
          onClick={openCalendar}
          disabled={disabled}
          type="button"
          className="pmb-calendar-btn"
          data-testid={`${testId}-calendar`}
        >
          <Calendar size={18} />
        </Button>
      </InputGroup>

      <div style={{ position: 'relative', visibility: 'hidden', height: 0 }}>
        <Flatpickr
          ref={flatpickrRef}
          value={selectedDate}
          onChange={handleCalendarChange}
          options={{
            mode: 'single',
            dateFormat: 'd/m/Y',
            monthSelectorType: 'static',
            appendTo: document.body,
            position: 'below',
          }}
          disabled={disabled}
        />
      </div>
    </div>
  )
}

/**
 * PMB Month Picker Component
 * @param {string} value - Month string in 'YYYY-MM' format
 * @param {function} onChange - Callback when month is valid, returns formatted month string
 * @param {string} label - Input label
 * @param {boolean} disabled - Disable input
 * @param {string} format - Output format: 'dd-mm-yyyy' | 'yyyy-mm-dd' | 'iso' (default: 'yyyy-mm-dd')
 */
export const PMBMonthPicker = ({
  value,
  onChange,
  label = 'Bulan',
  disabled = false,
  format = 'yyyy-mm-dd',
  testId = 'pmb-month',
  ...props
}) => {
  const [selectedDate, setSelectedDate] = useState([])
  const [inputValue, setInputValue] = useState('')
  const flatpickrRef = useRef(null)
  const inputRef = useRef(null)
  // Month grid instead of a day calendar; memoized so flatpickr isn't re-created each render
  const monthPlugins = useMemo(
    () => [new MonthSelectPlugin({ shorthand: true, dateFormat: 'm/Y' })],
    [],
  )

  // Initialize from external value prop
  useEffect(() => {
    if (value) {
      const [year, month] = value.split('-')
      const date = new Date(year, parseInt(month) - 1, 1)
      setSelectedDate([date])
      setInputValue(monthLabel(date))
    } else {
      setSelectedDate([])
      setInputValue('')
    }
  }, [value])

  /**
   * Called when flatpickr calendar selection changes
   */
  const handleCalendarChange = (selectedDates) => {
    if (selectedDates.length > 0) {
      let date = selectedDates[0]

      // Always apply time adjustment
      date = setStartOfDay(date)

      setSelectedDate([date])

      setInputValue(monthLabel(date))

      // Call onChange with requested format
      const dateFormatted = formatDateWithFormat(date, format)
      if (onChange) {
        onChange(dateFormatted)
      }
    }
  }

  /**
   * Called when input is focused - open flatpickr
   */
  const handleInputFocus = () => {
    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.open()
    }
  }

  /**
   * Open calendar picker
   */
  const openCalendar = () => {
    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.open()
    }
  }

  /**
   * Clear month
   */
  const handleClear = () => {
    setInputValue('')
    setSelectedDate([])
    if (onChange) {
      onChange(null)
    }
  }

  return (
    <div className="pmb-month-picker" data-testid={testId} {...props}>
      {label && (
        <label className="d-block mb-2" data-testid={`${testId}-label`}>
          {label}
        </label>
      )}

      <InputGroup>
        <input
          ref={inputRef}
          readOnly
          value={inputValue}
          onClick={openCalendar}
          onFocus={handleInputFocus}
          placeholder="Pilih bulan"
          disabled={disabled}
          className="form-control pmb-month-input"
          data-testid={`${testId}-input`}
        />
        {inputValue !== '' && (
          <Button
            color="secondary"
            outline
            onClick={handleClear}
            disabled={disabled}
            type="button"
            className="pmb-clear-btn"
            data-testid={`${testId}-clear`}
          >
            <X size={18} />
          </Button>
        )}
        <Button
          color="secondary"
          outline
          onClick={openCalendar}
          disabled={disabled}
          type="button"
          className="pmb-calendar-btn"
          data-testid={`${testId}-calendar`}
        >
          <Calendar size={18} />
        </Button>
      </InputGroup>

      <div style={{ position: 'relative', visibility: 'hidden', height: 0 }}>
        <Flatpickr
          ref={flatpickrRef}
          value={selectedDate}
          onChange={handleCalendarChange}
          options={{
            mode: 'single',
            dateFormat: 'm/Y',
            monthSelectorType: 'static',
            appendTo: document.body,
            position: 'below',
            plugins: monthPlugins,
          }}
          disabled={disabled}
        />
      </div>
    </div>
  )
}

export default PMBDatePicker

    